import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoginFormData } from '@/types';

const LoginPage = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  // CR-17: two-factor second step
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const { login, complete2FALogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Show success message if coming from reset password
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);

    if (result.success && result.requires2FA) {
      // CR-17: pause for the two-factor code
      setTwoFactorToken(result.tempToken || null);
    } else if (result.success) {
      setTimeout(() => {
        window.location.href = '/admin/projects';
      }, 100);
    } else {
      setError(result.error || 'Login failed');
    }

    setIsLoading(false);
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorToken) return;
    setIsLoading(true);
    setError('');

    const result = await complete2FALogin(twoFactorToken, code.trim());

    if (result.success) {
      setTimeout(() => {
        window.location.href = '/admin/projects';
      }, 100);
    } else {
      setError(result.error || 'Verification failed');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden noise-texture">
      {/* Background */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8FAFC] via-[#EFF6FF] to-[#DBEAFE]"></div>
        {/* Decorative orbs */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#18A5F9]/8 to-transparent blur-3xl"></div>
        <div className="absolute -bottom-48 -left-32 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-[#18A5F9]/5 to-transparent blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-white/60 to-transparent"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div
          className={`w-full max-w-[420px] transition-all duration-700 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Logo + Title */}
          <div className="text-center mb-10">
            <div className="inline-block mb-6">
              <img
                src="/assets/logo.svg"
                alt="Research Clever"
                className="h-20 w-auto"
              />
            </div>
            <p className="text-[15px] text-[#6B7280] tracking-wide">
              Supplier Management Platform
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-white/60">
            {!twoFactorToken && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
                  Email
                </label>
                <div className="md-outlined-textfield">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
                  Password
                </label>
                <div className="md-outlined-textfield">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="w-full"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {success && (
                <div className="flex items-center gap-2 text-emerald-700 text-sm bg-emerald-50 p-3 border border-emerald-200 rounded-xl">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {success}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="md-btn md-btn-primary w-full text-[15px] h-12 mt-2"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            )}

            {twoFactorToken && (
            <form onSubmit={handleVerify2FA} className="space-y-5">
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold font-heading text-[#1A1F2B] mb-1">Two-factor authentication</h2>
                <p className="text-sm text-[#6B7280]">
                  Enter the 6-digit code from your authenticator app, or a recovery code.
                </p>
              </div>
              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
                  Authentication code
                </label>
                <div className="md-outlined-textfield">
                  <input
                    type="text"
                    name="code"
                    value={code}
                    onChange={(e) => { setCode(e.target.value); if (error) setError(''); }}
                    placeholder="123456"
                    className="w-full"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="md-btn md-btn-primary w-full text-[15px] h-12 mt-2"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify & Sign In'
                )}
              </button>

              <button
                type="button"
                onClick={() => { setTwoFactorToken(null); setCode(''); setError(''); }}
                className="text-[#6B7280] hover:text-[#374151] text-sm font-medium transition-colors w-full text-center"
              >
                Back to login
              </button>
            </form>
            )}
          </div>

          {/* Footer link */}
          <div className="text-center mt-6">
            <Link
              to="/admin/forgot-password"
              className="text-[#18A5F9] hover:text-[#0D8FDE] text-sm font-semibold transition-colors"
            >
              Forgot your password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
